import React, { useState, useEffect } from 'react';
import { DollarSign, Download, Loader2, TrendingUp, Users, Clock, PieChart } from 'lucide-react';
import api from '../../services/api';

const monthLabel = (m) => {
  const [y, mo] = m.split('-').map(Number);
  return new Date(y, mo - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

const RevenueAnalytics = () => {
  const now = new Date();
  const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const [month, setMonth] = useState(defaultMonth);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');

  const load = async (m) => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get(`/admin/revenue/monthly?month=${m}`);
      setReport(data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load revenue report.');
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(month); }, [month]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/admin/revenue/monthly.pdf?month=${month}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `PulsePortal_Revenue_${month}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Download failed.');
    } finally {
      setDownloading(false);
    }
  };

  // Build a list of the last 12 months as options
  const monthOptions = React.useMemo(() => {
    const arr = [];
    const d = new Date(now.getFullYear(), now.getMonth(), 1);
    for (let i = 0; i < 12; i++) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      arr.push(`${y}-${m}`);
      d.setMonth(d.getMonth() - 1);
    }
    return arr;
  }, [now]);

  return (
    <div className="space-y-6" data-testid="revenue-analytics">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-foreground flex items-center space-x-2">
            <DollarSign className="h-6 w-6 text-electric" />
            <span>Revenue Analytics</span>
          </h2>
          <p className="text-sm text-muted-foreground">
            Subscription revenue split — Platform 30% · Creator Pool 70% · Distributed by watch-time share
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="px-3 py-2 rounded-lg bg-white/5 border border-white/20 text-foreground focus:outline-none focus:ring-2 focus:ring-electric"
            data-testid="revenue-month-select"
          >
            {monthOptions.map((m) => (
              <option key={m} value={m} className="bg-background">{monthLabel(m)}</option>
            ))}
          </select>
          <button
            onClick={downloadPdf}
            disabled={downloading || !report}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-electric text-background font-semibold hover:bg-electric/90 disabled:opacity-50"
            data-testid="download-revenue-pdf"
          >
            {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            <span>Download PDF</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm">{error}</div>
      )}

      {loading && (
        <div className="p-8 text-center text-muted-foreground">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2 text-electric" /> Loading…
        </div>
      )}

      {!loading && report && (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KPI icon={Users} label="Subscribers" value={report.subscribers.toLocaleString()} />
            <KPI icon={TrendingUp} label="Gross Revenue" value={`$${report.gross_revenue_usd.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}`} accent />
            <KPI icon={PieChart} label={`Platform (${report.platform_cut_pct}%)`} value={`$${report.platform_share_usd.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}`} />
            <KPI icon={DollarSign} label={`Creator Pool (${report.creator_pool_pct}%)`} value={`$${report.creator_pool_usd.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}`} accent />
          </div>

          {/* Watch stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <KPI icon={Clock} label="Total Watch Hours" value={`${report.total_watch_hours.toLocaleString()} h`} />
            <KPI icon={PieChart} label="Titles With Activity" value={report.titles.length.toLocaleString()} />
            <KPI icon={DollarSign} label="Sub Price" value={`$${report.subscription_price_usd.toFixed(2)} / mo`} />
          </div>

          {/* Per-title table */}
          <div className="rounded-2xl border border-white/10 overflow-hidden">
            <div className="px-4 py-3 border-b border-white/10 bg-white/[0.02]">
              <h3 className="font-semibold text-foreground">Per-Title Payouts — {report.month_label}</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="revenue-title-table">
                <thead className="bg-white/[0.03] text-muted-foreground">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">#</th>
                    <th className="text-left px-4 py-2 font-medium">Title</th>
                    <th className="text-left px-4 py-2 font-medium">Director / Creator</th>
                    <th className="text-left px-4 py-2 font-medium">Type</th>
                    <th className="text-right px-4 py-2 font-medium">Watch Hours</th>
                    <th className="text-right px-4 py-2 font-medium">Share</th>
                    <th className="text-right px-4 py-2 font-medium">Payout</th>
                  </tr>
                </thead>
                <tbody>
                  {report.titles.length === 0 ? (
                    <tr><td colSpan="7" className="px-4 py-8 text-center text-muted-foreground">No watch activity recorded for this month yet.</td></tr>
                  ) : report.titles.map((t, i) => (
                    <tr key={t.content_id} className="border-t border-white/5 hover:bg-white/[0.02]">
                      <td className="px-4 py-2 text-muted-foreground">{i + 1}</td>
                      <td className="px-4 py-2 font-medium text-foreground">{t.title}</td>
                      <td className="px-4 py-2 text-muted-foreground">{t.director}</td>
                      <td className="px-4 py-2 text-muted-foreground capitalize">{t.content_type.replace('_', ' ')}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{t.watch_hours.toFixed(2)}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-electric">{t.watch_share_pct.toFixed(2)}%</td>
                      <td className="px-4 py-2 text-right tabular-nums font-semibold text-foreground">${t.payout_usd.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <p className="text-xs text-muted-foreground">
            Watch time is aggregated from user watch progress records last updated within the selected month.
            The full formula and per-title payouts are included in the downloadable PDF report.
          </p>
        </>
      )}
    </div>
  );
};

const KPI = ({ icon: Icon, label, value, accent = false }) => (
  <div className={`rounded-2xl border border-white/10 p-4 ${accent ? 'bg-electric/10' : 'bg-white/[0.02]'}`}>
    <div className="flex items-center gap-2 text-muted-foreground text-xs mb-2">
      <Icon className="h-4 w-4" />
      <span>{label}</span>
    </div>
    <div className={`text-2xl font-bold ${accent ? 'text-electric' : 'text-foreground'} tabular-nums`}>{value}</div>
  </div>
);

export default RevenueAnalytics;
